import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Camera, Sparkles, Store, MessageCircle, CreditCard, ShieldCheck, Check, ArrowRight,
  LogIn, ChevronDown, Zap, Award, Clock, Play, Star, TrendingUp, Package, Users, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Placeholder demo video — l'admin può cambiarlo dalle impostazioni piattaforma
const DEFAULT_VIDEO_EMBED = 'https://www.youtube.com/embed/LXb3EKWsInQ?autoplay=1&rel=0';

const FAKE_TESTIMONIALS = [
  { name: 'Carmela', shop: 'Biancheria di Napoli', text: 'Carico 30 prodotti al giorno in 15 minuti. Prima ci mettevo 3 ore.', rating: 5 },
  { name: 'Giovanni', shop: 'Casalinghi Roma', text: 'Il volantino su WhatsApp porta il doppio dei clienti in negozio.', rating: 5 },
  { name: 'Lucia', shop: 'Moda 2026', text: 'L\'AI fa le foto più belle delle mie. Sembrano scattate da un professionista.', rating: 5 },
];

const FAQ = [
  { q: 'Devo avere competenze tecniche?', a: 'No. Se sai scattare una foto col cellulare, sai usare PhotoVendo. L\'AI fa tutto il resto.' },
  { q: 'Posso cambiare piano in qualsiasi momento?', a: 'Sì, contatta il nostro supporto e aggiorneremo il tuo abbonamento. Nessun vincolo o penale.' },
  { q: 'I miei clienti devono installare un\'app?', a: 'No. Il tuo negozio è un sito web normale. Condividi il link su WhatsApp e i clienti comprano dal browser.' },
  { q: 'Come ricevo i pagamenti?', a: 'Direttamente sul tuo account Stripe o PayPal. Noi non tratteniamo nulla sulle tue vendite.' },
  { q: 'Posso provare gratis?', a: 'Contatta il supporto per una demo guidata. Puoi aprire l\'account ora e pagare solo quando siamo pronti.' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [shops, setShops] = useState([]);
  const [landing, setLanding] = useState({});
  const [accessOpen, setAccessOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const fetchAll = useCallback(async () => {
    try {
      const [planRes, shopRes, landingRes] = await Promise.all([
        axios.get(`${API}/public/subscription-plans`),
        axios.get(`${API}/public/showcase-shops?limit=12`),
        axios.get(`${API}/public/landing-info`),
      ]);
      setPlans(planRes.data.plans || []);
      setAddons(planRes.data.addons || []);
      setShops(shopRes.data.shops || []);
      setLanding(landingRes.data.landing || {});
    } catch { toast.error('Errore caricamento'); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  const openShop = (slug) => navigate(`/negozio/${slug}`);

  const stats = [
    { value: shops.length || '—', label: 'Negozi attivi', Icon: Store, color: 'text-[#C25934]' },
    { value: '500+', label: 'Prodotti creati/giorno', Icon: Package, color: 'text-[#8B5CF6]' },
    { value: '30s', label: 'Da foto a prodotto', Icon: Zap, color: 'text-[#D98324]' },
    { value: '12 ore', label: 'Risparmio settimanale', Icon: Clock, color: 'text-[#4A6B53]' },
  ];

  const videoEmbed = landing.video_embed_url || DEFAULT_VIDEO_EMBED;

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#FDFBF7] overflow-x-hidden">
      {/* HEADER */}
      <header className="sticky top-0 z-40 bg-[#FDFBF7]/90 backdrop-blur-lg border-b border-[#E8E3D9]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => scrollTo('hero')} className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-to-br from-[#C25934] to-[#D98324] rounded-xl flex items-center justify-center shadow-md">
              <Camera className="text-white" size={20} />
            </div>
            <span className="font-bold text-lg text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>PhotoVendo</span>
          </button>
          <nav className="hidden md:flex items-center gap-6">
            <button onClick={() => scrollTo('come-funziona')} className="text-sm text-[#5C554F] hover:text-[#C25934]">Come funziona</button>
            <button onClick={() => scrollTo('video-demo')} className="text-sm text-[#5C554F] hover:text-[#C25934]">Demo</button>
            <button onClick={() => scrollTo('piani')} className="text-sm text-[#5C554F] hover:text-[#C25934]">Piani</button>
            <button onClick={() => scrollTo('vetrina')} className="text-sm text-[#5C554F] hover:text-[#C25934]">Vetrina</button>
          </nav>
          <div className="flex items-center gap-2">
            <Button
              data-testid="header-login-btn"
              onClick={() => navigate('/account')}
              className="h-9 px-4 rounded-full bg-[#2A2624] hover:bg-[#1a1817] text-white text-sm"
            >
              <LogIn size={14} className="mr-1.5" /> Accedi
            </Button>
            {user && (
              <Button data-testid="my-dashboard-btn" onClick={() => navigate(user.role === 'superadmin' ? '/superadmin' : '/admin')} className="h-9 px-3 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white text-sm hidden sm:inline-flex">
                Dashboard
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section id="hero" className="relative pt-8 sm:pt-14 pb-20 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh pointer-events-none" />
        <div className="absolute inset-0 animated-grid pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
          <div className="anim-float-up">
            <div className="inline-flex items-center gap-1.5 bg-white border border-[#C25934]/20 text-[#C25934] px-3 py-1 rounded-full text-xs font-semibold shadow-sm">
              <Zap size={12} /> AI per cataloghi giornalieri
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2A2624] leading-[1.05]" style={{ fontFamily: 'Outfit' }}>
              {landing.hero_title || (
                <>
                  Trasforma<br />
                  <span className="bg-gradient-to-r from-[#C25934] via-[#D98324] to-[#8B5CF6] bg-clip-text text-transparent">le foto dal cellulare</span><br />
                  in un e-commerce.
                </>
              )}
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-[#5C554F] max-w-xl">
              {landing.hero_subtitle || 'Tu scatti. L\'AI crea titolo, prezzo e foto pulite. In 30 secondi il prodotto è online, pronto da condividere su WhatsApp.'}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button data-testid="cta-open-shop" onClick={() => navigate('/apri-negozio')} className="h-14 px-7 rounded-full bg-gradient-to-r from-[#C25934] to-[#D98324] hover:opacity-95 text-white font-semibold text-base cta-glow">
                <Store size={18} className="mr-2" /> Apri il tuo negozio <ArrowRight size={16} className="ml-2" />
              </Button>
              <Button data-testid="cta-watch-video" onClick={() => setVideoOpen(true)} variant="outline" className="h-14 px-7 rounded-full border-2 border-[#2A2624] text-[#2A2624] hover:bg-[#2A2624] hover:text-white font-semibold text-base">
                <Play size={18} className="mr-2" fill="currentColor" /> Guarda demo · 60s
              </Button>
            </div>
            <div className="mt-6 flex items-center gap-4 text-sm text-[#877E78]">
              <div className="flex -space-x-2">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className={`w-8 h-8 rounded-full border-2 border-[#FDFBF7] flex items-center justify-center text-white text-[10px] font-bold`} style={{ background: ['#C25934', '#8B5CF6', '#4A6B53', '#D98324'][i] }}>
                    {['C', 'G', 'L', 'M'][i]}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-0.5">
                  {[0, 1, 2, 3, 4].map(i => <Star key={i} size={12} className="fill-[#D98324] text-[#D98324]" />)}
                </div>
                <span className="text-xs"><b>4.9/5</b> · {shops.length || '100+'} negozianti felici</span>
              </div>
            </div>
          </div>

          {/* PHONE MOCKUP */}
          <div className="relative flex justify-center anim-float-up" style={{ animationDelay: '0.2s' }}>
            <div className="relative">
              {/* Floating badges */}
              <div className="absolute -top-3 -left-4 bg-white rounded-2xl shadow-xl px-3 py-2 flex items-center gap-2 anim-soft-bounce z-10">
                <div className="w-8 h-8 bg-[#4A6B53]/10 rounded-lg flex items-center justify-center">
                  <Check size={18} className="text-[#4A6B53]" />
                </div>
                <div>
                  <div className="text-[10px] text-[#877E78] uppercase font-bold">Pubblicato</div>
                  <div className="text-xs font-bold text-[#2A2624]">in 28s</div>
                </div>
              </div>
              <div className="absolute -bottom-2 -right-4 bg-white rounded-2xl shadow-xl px-3 py-2 flex items-center gap-2 anim-soft-bounce z-10" style={{ animationDelay: '0.5s' }}>
                <div className="w-8 h-8 bg-[#8B5CF6]/10 rounded-lg flex items-center justify-center">
                  <Sparkles size={16} className="text-[#8B5CF6]" />
                </div>
                <div>
                  <div className="text-[10px] text-[#877E78] uppercase font-bold">AI ha creato</div>
                  <div className="text-xs font-bold text-[#2A2624]">6 varianti</div>
                </div>
              </div>
              {/* Phone frame */}
              <div className="w-[280px] sm:w-[320px] h-[560px] sm:h-[640px] bg-[#1a1a1a] rounded-[40px] p-3 shadow-2xl border-[3px] border-[#2a2a2a]">
                <div className="w-full h-full rounded-[32px] overflow-hidden bg-[#FDFBF7] relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#1a1a1a] rounded-b-2xl z-20" />
                  {/* Animated screens */}
                  <div className="w-full h-full overflow-hidden relative">
                    <div className="phone-screen-slide" style={{ height: '300%' }}>
                      <PhoneScreen1 />
                      <PhoneScreen2 />
                      <PhoneScreen3 />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="relative bg-gradient-to-r from-[#2A2624] via-[#3d3631] to-[#2A2624] py-8 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <div key={i} className="text-center anim-float-up" style={{ animationDelay: `${i * 0.1}s` }}>
              <s.Icon size={24} className={`${s.color} mx-auto mb-1`} />
              <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{s.value}</div>
              <div className="text-[11px] text-[#C25934]/80 uppercase tracking-wider font-semibold">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* VIDEO DEMO */}
      <section id="video-demo" className="max-w-5xl mx-auto px-4 py-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 bg-[#C25934]/10 text-[#C25934] px-3 py-1 rounded-full text-xs font-semibold mb-3">
            <Play size={12} fill="currentColor" /> VIDEO DEMO
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            Vedi PhotoVendo in azione
          </h2>
          <p className="mt-2 text-[#5C554F]">60 secondi che cambiano il modo di vendere nel tuo negozio.</p>
        </div>
        <button
          data-testid="video-play-btn"
          onClick={() => setVideoOpen(true)}
          className="group relative w-full aspect-video rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#2A2624] via-[#5C554F] to-[#C25934] cursor-pointer"
        >
          {/* Thumbnail overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white/95 rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
              <Play size={40} className="text-[#C25934] ml-1" fill="currentColor" />
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-6 left-6 text-left">
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-full mb-2">
              ▶︎ 1 min 12s
            </span>
            <h3 className="text-white text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
              Dal primo scatto al prodotto pubblicato
            </h3>
          </div>
        </button>
      </section>

      {/* HOW IT WORKS */}
      <section id="come-funziona" className="bg-white border-y border-[#E8E3D9] py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1.5 bg-[#8B5CF6]/10 text-[#8B5CF6] px-3 py-1 rounded-full text-xs font-semibold mb-3">
              COME FUNZIONA
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
              Tre step. Nient'altro.
            </h2>
            <p className="mt-2 text-[#5C554F] max-w-xl mx-auto">Dal telefono del negoziante al sito pubblico in meno di 1 minuto.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <StepCard n={1} Icon={Camera} title="Scatti" desc="Scatta da 1 a 6 foto del prodotto col cellulare. Il flow guidato ti accompagna passo-passo." color="#C25934" />
            <StepCard n={2} Icon={Sparkles} title="AI crea" desc="Legge cartellini, prezzi, varianti. Pulisce le foto mettendole su sfondo bianco professionale." color="#8B5CF6" />
            <StepCard n={3} Icon={Store} title="Pubblichi" desc="Volantino giornaliero + catalogo online + auto-post Telegram. Condividi con 1 click." color="#4A6B53" />
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            Tutto incluso, niente da installare
          </h2>
          <p className="mt-2 text-[#5C554F]">Tecnologia pronta. Tu pensi a vendere.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          <BenefitCard Icon={Store} title="Sito dedicato" desc="URL personalizzato condivisibile" tone="#C25934" />
          <BenefitCard Icon={MessageCircle} title="Volantino WhatsApp" desc="Condivisibile in 1 click ogni giorno" tone="#25D366" />
          <BenefitCard Icon={Zap} title="Telegram Bot" desc="Auto-posting nei tuoi canali" tone="#0088cc" />
          <BenefitCard Icon={CreditCard} title="Stripe + PayPal" desc="Pagamenti con carta integrati" tone="#635bff" />
          <BenefitCard Icon={Clock} title="Supporto prioritario" desc="WhatsApp diretto per admin Pro" tone="#4A6B53" />
          <BenefitCard Icon={Sparkles} title="AI Foto Pro" desc="Ottimizzazione illimitata (add-on)" tone="#8B5CF6" />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#F7F3EC] py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
              Parlano di noi i nostri clienti
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {FAKE_TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white rounded-3xl p-6 border border-[#E8E3D9] relative">
                <div className="absolute -top-4 left-6 bg-gradient-to-br from-[#C25934] to-[#D98324] text-white w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg shadow-lg" style={{ fontFamily: 'Outfit' }}>
                  {t.name[0]}
                </div>
                <div className="pt-6">
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: t.rating }).map((_, j) => <Star key={j} size={14} className="fill-[#D98324] text-[#D98324]" />)}
                  </div>
                  <p className="text-[#2A2624] italic leading-relaxed">"{t.text}"</p>
                  <div className="mt-4 pt-3 border-t border-[#E8E3D9]">
                    <div className="font-bold text-[#2A2624]">{t.name}</div>
                    <div className="text-xs text-[#877E78]">{t.shop}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWCASE */}
      <section id="vetrina" className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 bg-[#D98324]/10 text-[#D98324] px-3 py-1 rounded-full text-xs font-semibold mb-3">
            <Award size={12} /> VETRINA LIVE
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            I negozi che già usano PhotoVendo
          </h2>
        </div>
        {shops.length === 0 ? (
          <div className="text-center text-[#877E78]">
            Stiamo selezionando i primi negozi. <button onClick={() => navigate('/apri-negozio')} className="text-[#C25934] font-semibold underline">Sii tu il primo →</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {shops.map(s => <ShopShowcaseCard key={s.id} shop={s} onClick={() => openShop(s.slug)} />)}
          </div>
        )}
      </section>

      {/* PIANI */}
      <section id="piani" className="bg-white border-y border-[#E8E3D9] py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1.5 bg-[#4A6B53]/10 text-[#4A6B53] px-3 py-1 rounded-full text-xs font-semibold mb-3">
              PRICING SEMPLICE
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
              Scegli il tuo piano
            </h2>
            <p className="mt-2 text-[#5C554F]">Mensili, senza vincoli. Add-on opzionali a parte.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {plans.map(p => <PlanCard key={p.id} plan={p} onChoose={() => navigate(`/apri-negozio?piano=${p.id}`)} />)}
          </div>
          {addons.length > 0 && (
            <div className="mt-12">
              <h3 className="text-lg font-bold text-[#2A2624] mb-4 text-center" style={{ fontFamily: 'Outfit' }}>
                Add-on opzionali · sommabili a qualsiasi piano
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl mx-auto">
                {addons.map(a => (
                  <div key={a.id} data-testid={`addon-${a.id}`} className="bg-[#F7F3EC] rounded-xl border border-[#E8E3D9] p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-[#2A2624] text-sm">{a.name}</span>
                      <span className="text-sm font-bold text-[#C25934]">+€{a.price_monthly}/mese</span>
                    </div>
                    <p className="text-xs text-[#877E78]">{a.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 py-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2624] text-center mb-8" style={{ fontFamily: 'Outfit' }}>
          Domande frequenti
        </h2>
        <div className="space-y-3">
          {FAQ.map((item, i) => (
            <div key={i} className="bg-white rounded-xl border border-[#E8E3D9] overflow-hidden">
              <button
                data-testid={`faq-q-${i}`}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full px-4 py-4 text-left flex items-center justify-between gap-3 hover:bg-[#F7F3EC] transition-colors"
              >
                <span className="font-semibold text-[#2A2624]">{item.q}</span>
                <ChevronDown size={18} className={`text-[#C25934] flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === i && (
                <div className="px-4 pb-4 text-sm text-[#5C554F] leading-relaxed">{item.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#C25934] via-[#D98324] to-[#8B5CF6]" />
        <div className="absolute inset-0 animated-grid opacity-50" />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white leading-tight" style={{ fontFamily: 'Outfit' }}>
            Pronto a vendere<br />più velocemente?
          </h2>
          <p className="mt-4 text-white/90 text-lg">
            Apri il tuo negozio in 3 minuti. Nessuna carta richiesta per provare.
          </p>
          <Button
            data-testid="final-cta"
            onClick={() => navigate('/apri-negozio')}
            className="mt-7 h-14 px-8 rounded-full bg-white text-[#C25934] hover:bg-[#FDFBF7] font-bold text-base shadow-2xl"
          >
            Apri il tuo negozio ora <ArrowRight size={18} className="ml-2" />
          </Button>
          <p className="mt-4 text-white/70 text-sm">
            <TrendingUp size={14} className="inline mr-1" /> Unisciti a <b>{shops.length || '100+'}</b> negozianti che già usano PhotoVendo
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#2A2624] text-[#E8E3D9] py-12 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 bg-gradient-to-br from-[#C25934] to-[#D98324] rounded-xl flex items-center justify-center">
                <Camera className="text-white" size={20} />
              </div>
              <span className="font-bold text-lg text-white" style={{ fontFamily: 'Outfit' }}>PhotoVendo</span>
            </div>
            <p className="text-sm text-[#877E78] max-w-md">
              La piattaforma e-commerce per negozi con catalogo giornaliero.<br />
              Scatti. Pubblichi. Vendi.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Prodotto</h4>
            <ul className="space-y-2 text-xs">
              <li><button onClick={() => scrollTo('come-funziona')} className="hover:text-white">Come funziona</button></li>
              <li><button onClick={() => scrollTo('video-demo')} className="hover:text-white">Video demo</button></li>
              <li><button onClick={() => scrollTo('piani')} className="hover:text-white">Piani e prezzi</button></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Legale</h4>
            <ul className="space-y-2 text-xs">
              <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-white">Termini di servizio</a></li>
              <li><a href="#" className="hover:text-white">Cookie</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-8 pt-6 border-t border-white/10 text-center text-xs text-[#877E78]">
          © 2026 PhotoVendo · Tutti i diritti riservati · Made in Italy 🇮🇹
        </div>
      </footer>

      {/* VIDEO MODAL */}
      {videoOpen && (
        <div data-testid="video-modal" className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setVideoOpen(false)}>
          <button
            data-testid="close-video"
            onClick={() => setVideoOpen(false)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
          >
            <X size={20} />
          </button>
          <div className="w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <iframe
              src={videoEmbed}
              title="PhotoVendo Demo"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ============= Subcomponents ============= */

function StepCard({ n, Icon, title, desc, color }) {
  return (
    <div className="relative bg-white rounded-3xl border border-[#E8E3D9] p-6 hover:shadow-xl transition-shadow duration-300 anim-float-up" style={{ animationDelay: `${n * 0.1}s` }}>
      <div className="absolute -top-4 -left-4 w-10 h-10 text-white rounded-full flex items-center justify-center font-bold shadow-lg" style={{ backgroundColor: color, fontFamily: 'Outfit' }}>{n}</div>
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: color + '15' }}>
        <Icon size={28} style={{ color }} />
      </div>
      <h3 className="font-bold text-[#2A2624] text-xl" style={{ fontFamily: 'Outfit' }}>{title}</h3>
      <p className="text-sm text-[#5C554F] mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}

function BenefitCard({ Icon, title, desc, tone }) {
  return (
    <div className="bg-white rounded-2xl p-4 border border-[#E8E3D9] hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2" style={{ backgroundColor: tone + '18' }}>
        <Icon size={20} style={{ color: tone }} />
      </div>
      <h4 className="font-bold text-[#2A2624] text-sm">{title}</h4>
      <p className="text-xs text-[#877E78] mt-0.5">{desc}</p>
    </div>
  );
}

function ShopShowcaseCard({ shop, onClick }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const coverUrl = shop.cover_path ? `${backend}/api/files/${shop.cover_path}` : null;
  return (
    <button
      data-testid={`showcase-shop-${shop.slug}`}
      onClick={onClick}
      className="text-left bg-white rounded-3xl border border-[#E8E3D9] overflow-hidden hover:shadow-2xl hover:-translate-y-1 hover:border-[#C25934]/30 transition-all group"
    >
      <div className="relative aspect-video bg-gradient-to-br from-[#C25934]/20 to-[#8B5CF6]/20">
        {coverUrl ? (
          <img src={coverUrl} alt={shop.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Store size={42} className="text-[#C25934]/50" />
          </div>
        )}
        {shop.featured && (
          <span className="absolute top-3 right-3 bg-[#C25934] text-white text-[10px] font-bold px-2 py-1 rounded-full shadow">
            <Award size={10} className="inline mr-0.5" /> IN EVIDENZA
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-bold text-[#2A2624] text-lg" style={{ fontFamily: 'Outfit' }}>{shop.name}</h3>
        {shop.city && <p className="text-xs text-[#877E78] mt-0.5">📍 {shop.city}</p>}
        {shop.description && <p className="text-sm text-[#5C554F] mt-2 line-clamp-2">{shop.description}</p>}
        <div className="mt-3 flex items-center gap-1 text-sm text-[#C25934] font-semibold group-hover:underline">
          Visita il negozio <ArrowRight size={14} />
        </div>
      </div>
    </button>
  );
}

function PlanCard({ plan, onChoose }) {
  const popular = plan.popular || plan.id === 'business';
  return (
    <div data-testid={`plan-card-${plan.id}`} className={`relative bg-white rounded-3xl p-6 transition-all duration-300 hover:-translate-y-1 ${popular ? 'border-2 border-[#C25934] shadow-2xl ring-4 ring-[#C25934]/10' : 'border border-[#E8E3D9] hover:shadow-xl'}`}>
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#C25934] to-[#D98324] text-white text-[10px] font-bold px-4 py-1 rounded-full shadow-lg">
          ⭐ PIÙ SCELTO
        </div>
      )}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>{plan.name}</h3>
        {plan.tagline && <p className="text-xs text-[#877E78] mt-0.5">{plan.tagline}</p>}
        <div className="mt-4">
          <span className="text-5xl font-bold text-[#C25934]" style={{ fontFamily: 'Outfit' }}>€{plan.price_monthly}</span>
          <span className="text-sm text-[#877E78]">/mese</span>
        </div>
        {plan.price_yearly && (
          <p className="text-[10px] text-[#4A6B53] font-semibold mt-1">o €{plan.price_yearly}/anno · 2 mesi gratis</p>
        )}
      </div>
      <ul className="mt-5 space-y-2 text-sm text-[#5C554F]">
        {(plan.features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <div className="w-5 h-5 bg-[#4A6B53]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check size={12} className="text-[#4A6B53]" />
            </div>
            {f}
          </li>
        ))}
      </ul>
      <Button
        data-testid={`choose-plan-${plan.id}`}
        onClick={onChoose}
        className={`w-full h-12 mt-6 rounded-full font-semibold ${popular ? 'bg-gradient-to-r from-[#C25934] to-[#D98324] hover:opacity-95 text-white cta-glow' : 'bg-[#2A2624] hover:bg-black text-white'}`}
      >
        Scegli {plan.name}
        <ArrowRight size={16} className="ml-2" />
      </Button>
    </div>
  );
}

/* ============= Phone Mockup Screens ============= */

function PhoneScreen1() {
  return (
    <div className="w-full h-1/3 relative bg-gradient-to-b from-[#FDFBF7] to-[#F0EBE1] p-4 pt-10">
      <div className="text-[10px] font-bold text-[#877E78] uppercase tracking-widest text-center mb-2">PhotoVendo · Scatto</div>
      <div className="relative aspect-[3/4] bg-[#1a1a1a] rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#8B5CF6]/30 via-transparent to-[#C25934]/30" />
        <div className="absolute top-3 left-3 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">● REC 0:14</div>
        <div className="absolute inset-8 border-2 border-white/80 rounded-xl" />
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-12 h-12 bg-white rounded-full border-4 border-white/30 shadow-lg" />
      </div>
      <div className="text-center text-xs text-[#5C554F] mt-2 font-medium">Scatto 3 di 6</div>
    </div>
  );
}

function PhoneScreen2() {
  return (
    <div className="w-full h-1/3 relative bg-[#FDFBF7] p-4 pt-10">
      <div className="text-[10px] font-bold text-[#877E78] uppercase tracking-widest text-center mb-2">AI Elaborazione</div>
      <div className="bg-gradient-to-br from-[#8B5CF6]/10 to-[#C25934]/10 rounded-2xl p-4 border border-[#8B5CF6]/20">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 bg-[#8B5CF6]/20 rounded-lg flex items-center justify-center">
            <Sparkles size={18} className="text-[#8B5CF6]" />
          </div>
          <div>
            <div className="text-xs font-bold text-[#2A2624]">Gemini AI analizza...</div>
            <div className="text-[10px] text-[#877E78]">6 foto · 28s</div>
          </div>
        </div>
        <div className="space-y-2">
          {['Titolo estratto', 'Prezzo €6,00', '6 varianti colore', 'Tag: biancheria, tende'].map((t, i) => (
            <div key={i} className="flex items-center gap-2 bg-white rounded-lg p-2 text-[10px]">
              <Check size={12} className="text-[#4A6B53]" />
              <span className="text-[#2A2624] font-medium">{t}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 h-1.5 bg-[#E8E3D9] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#C25934] rounded-full" style={{ width: '78%' }} />
        </div>
      </div>
    </div>
  );
}

function PhoneScreen3() {
  return (
    <div className="w-full h-1/3 relative bg-[#FDFBF7] p-4 pt-10">
      <div className="text-[10px] font-bold text-[#877E78] uppercase tracking-widest text-center mb-2">Prodotto Pubblicato</div>
      <div className="bg-white rounded-2xl border border-[#E8E3D9] overflow-hidden shadow-md">
        <div className="aspect-square bg-gradient-to-br from-[#F7F3EC] to-[#F0EBE1] relative">
          <div className="absolute top-2 right-2 bg-[#C25934] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">-33%</div>
          <div className="absolute inset-4 bg-white/70 rounded-xl flex items-center justify-center">
            <Store size={30} className="text-[#C25934]/60" />
          </div>
        </div>
        <div className="p-2">
          <div className="text-[11px] font-bold text-[#2A2624] truncate">Tende Jasper · Varianti</div>
          <div className="flex items-baseline gap-1 mt-0.5">
            <span className="text-sm font-bold text-[#C25934]">€6.00</span>
            <span className="text-[9px] text-[#877E78] line-through">€9.00</span>
          </div>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1">
        <div className="bg-[#25D366] text-white text-[9px] font-bold text-center py-1.5 rounded-lg">
          💬 WhatsApp
        </div>
        <div className="bg-[#0088cc] text-white text-[9px] font-bold text-center py-1.5 rounded-lg">
          ✈︎ Telegram
        </div>
      </div>
    </div>
  );
}
